<script setup lang="ts">
import type { SuggestionProps } from "@tiptap/suggestion";
import type { IFuseOptions } from "fuse.js";
import type { UserProfile } from "ls/features/user/utils";
import type { Paths } from "type-fest";
import type { MentionAttributes } from "./types";
import { sort } from "fast-sort";
import Fuse from "fuse.js";
import MenuLabel from "ls/components/primitives/menu/MenuLabel.vue";
import { useAuth } from "ls/features/user/useAuth";
import { getDisplayName } from "ls/features/user/useDisplayName";
import { computed, markRaw, toRaw, watchEffect } from "vue";
import { newGuid } from "@/common/lib/string";
import { useMentionSuggestionContext, useUserSuggestionsContext } from "./context";
import UserSuggestionListItem from "./UserSuggestionListItem.vue";

const props = defineProps<SuggestionProps>();

const userSearchOptions: IFuseOptions<UserProfile> = {
  threshold: 0.4,
  ignoreFieldNorm: true,
  keys: ["nickname", "email"] satisfies Paths<UserProfile>[],
  shouldSort: true,
};

const { profiles } = useUserSuggestionsContext();

const { user } = useAuth();

const usersSuggestions = computed(() => {
  if (!profiles.value) return [];
  return sort(profiles.value).by([
    { asc: profile => getDisplayName(profile).toLowerCase() },
  ]);
});

const userSearchIndex = computed(() => {
  // optimize fuse index by marking data as raw, so vue reactivity tracking could be skipped
  const suggestions = toRaw(usersSuggestions.value);
  const index = markRaw(new Fuse(suggestions, userSearchOptions));
  return index;
});

const userSearchResults = computed(() => {
  if (usersSuggestions.value.length === 0) return [];
  const limit = 10;
  if (!props.query) return usersSuggestions.value.slice(0, limit);
  return userSearchIndex.value.search(props.query, { limit }).map(x => x.item);
});

function selectUser(userSuggestion: UserProfile & { id: string }) {
  if (!user.value) {
    throw new Error("Failed to insert @mention. Current user is not authenticated");
  }
  const values: MentionAttributes = {
    id: userSuggestion.id,
    label: userSuggestion.nickname || userSuggestion.email,
    mentionerId: user.value.userId,
    mentionId: newGuid(),
  };
  props.command(values);
}

const {
  setUserSearchResults,
  selectedIndex,
  select,
  selected,
} = useMentionSuggestionContext();

watchEffect(() => {
  setUserSearchResults(userSearchResults.value);
});

selected.on(index => {
  if (index <= userSearchResults.value.length - 1) {
    const result = userSearchResults.value[index];
    if (result) {
      selectUser(result);
    }
  }
});
</script>

<template>
  <div v-if="!user" class="p-1 text-slate-500 select-none">
    Please Sign in to @mention others
  </div>
  <template v-else>
    <MenuLabel v-if="userSearchResults.length > 0">
      People
    </MenuLabel>
    <UserSuggestionListItem
      v-for="(result, index) in userSearchResults"
      :key="result.id"
      :user="result"
      :data-highlighted="selectedIndex === index ? '' : undefined"
      @pointermove="select(index)"
      @click="selectUser(result)"
    />
  </template>
</template>
